import React, { useState } from 'react';
import { HeartHandshakeIcon, UsersRoundIcon, SparklesIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { Button, ButtonExternal, ButtonLink } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { DoodleUnderline } from '../components/brand/Doodles';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { externalLinks } from '../data/site';

// GIVING IS TEMPORARILY PAUSED.
//
// The supplied PayPal donation URL does not resolve (an issue with the BCM
// PayPal account), so the giving button no longer points at it. Instead it
// opens an "under construction" dialog. No payment form is ever rendered on
// this site, and no alternate processor has been invented.
//
// TODO: Once BCM supplies a working, permanent PayPal donation URL or hosted
// button code, restore `ButtonExternal href={externalLinks.paypalDonate}` here
// and remove the dialog.

const pillars = [{
  title: 'Bring In',
  copy: 'Welcome students and help them encounter the Gospel in a genuine Christian community.'
}, {
  title: 'Build Up',
  copy: 'Help students grow in faith, friendship, service, and leadership.'
}, {
  title: 'Send Out',
  copy: 'Equip students to live out their faith and serve on campus and beyond.'
}];
const otherWays = [{
  icon: UsersRoundIcon,
  title: 'Serve with us',
  copy: 'Students help lead worship, welcome new people, run outreach on campus, and serve at events throughout the year.'
}, {
  icon: SparklesIcon,
  title: 'Bring a friend',
  copy: 'One of the most valuable things anyone can do is invite a student who is looking for community to come on a Thursday.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Partner with BCM',
  copy: 'Churches and individuals who want to talk about partnering with BCM can reach the team directly by email.'
}];
export function Give() {
  const [givingNoticeOpen, setGivingNoticeOpen] = useState(false);
  return <>
      <PageHeader eyebrow="Give" title="Support ministry to college students" intro="UCF BCM is supported by churches and individuals who invest in Christian community and ministry among college students." data-id="element-515" data-mp="3fw8trgl2mctb" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="giving-heading" data-id="element-516" data-mp="3owx8kdiagh79">
        <DoodleScatter tone="white" seed="give-paypal" data-id="element-517" data-mp="2w8rpdv6vv09j" />
        <Container className="relative grid items-start gap-8 lg:grid-cols-[1fr_0.85fr] lg:gap-12" data-id="element-518" data-mp="2w9awshywq69b">
          <div className="rounded-[24px] border-2 border-brand-navy bg-brand-blue p-7 sm:p-9" data-id="element-519" data-mp="316t6wbonuriw">
            <h2 id="giving-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-[1.9rem]" data-id="element-520" data-mp="3605jfng2f7va">
              Give to UCF BCM
            </h2>
            <DoodleUnderline className="mt-3 h-3 w-36" color="var(--bcm-dark-blue)" data-id="element-521" data-mp="2dgguanih06lz" />
            <p className="mt-5 font-sans text-[16px] leading-relaxed text-ink" data-id="element-522" data-mp="25y94jn51q2q9">
              Online giving is being set up right now. Check back soon, or email the BCM team and
              someone will help you give in the meantime.
            </p>
            <Button onClick={() => setGivingNoticeOpen(true)} className="mt-7 min-h-[56px] px-8 py-4 text-[17px] sm:text-lg" data-id="element-523" data-mp="1rzsqtc8rnbym">
              Give to BCM
            </Button>
            <p className="mt-4 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-524" data-mp="1i8etnb4aeo8n">
              Questions about giving? Email the BCM team and someone will help.
            </p>
            <div className="mt-4" data-id="element-525" data-mp="18bs9kojsc17o">
              <ButtonExternal href={externalLinks.email} variant="secondary" data-id="element-526" data-mp="18clb9b3w00iy">
                Email About Giving
              </ButtonExternal>
            </div>
          </div>

          <div data-id="element-527" data-mp="zabz6gsyzz3g">
            <h2 className="font-serif text-2xl font-semibold text-brand-navy" data-id="element-528" data-mp="zbwrs0vq57yo">
              What your support makes possible
            </h2>
            <ul className="mt-6 space-y-5" data-id="element-529" data-mp="2zf7j2aq9qt7s">
              {pillars.map(pillar => <li key={pillar.title} className="border-l-4 border-brand-yellow pl-5" data-id="element-530" data-mp="33gjckrc9jyl9">
                  <h3 className="font-display text-xl font-bold text-brand-navy" data-id="element-531" data-mp="2pms1heivw6nf">{pillar.title}</h3>
                  <p className="mt-1.5 font-sans text-[15px] leading-relaxed text-ink" data-id="element-532" data-mp="2uka7aw1lu5r7">
                    {pillar.copy}
                  </p>
                </li>)}
            </ul>
          </div>
        </Container>
      </Section>

      <Section className="bcm-paper py-14 sm:py-18" labelledBy="other-ways-heading" data-id="element-533" data-mp="26opd91y5udqo">
        <Container data-id="element-534" data-mp="2bmzb5kb9rsfz">
          <h2 id="other-ways-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl" data-id="element-535" data-mp="2bmh8a4u3qfz3">
            Other ways to support BCM
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-536" data-mp="9vdx9egauze0">
            {otherWays.map(way => <li key={way.title} className="rounded-[22px] border-2 border-brand-navy bg-white p-6" data-id="element-537" data-mp="k0ch5qmonz0a">
                <way.icon aria-hidden="true" className="h-6 w-6 text-brand-navy" data-id="element-538" data-mp="f2ko2taay6ui" />
                <h3 className="mt-3 font-display text-xl font-bold leading-snug text-brand-navy" data-id="element-539" data-mp="ssxuusbgjfi9">
                  {way.title}
                </h3>
                <p className="mt-2 font-sans text-[15px] leading-relaxed text-ink" data-id="element-540" data-mp="12qby1q8de7sm">{way.copy}</p>
              </li>)}
          </ul>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-541" data-mp="xqfu1qzoagd2">
            <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-542" data-mp="1bra4sga70tew">Get Connected</ButtonExternal>
            <ButtonLink to="/about" variant="secondary" data-id="element-543" data-mp="17ovjzqmyrjkz">
              Learn More About BCM
            </ButtonLink>
          </div>
        </Container>
      </Section>

      <Modal open={givingNoticeOpen} onClose={() => setGivingNoticeOpen(false)} title="Under construction" data-id="element-544" data-mp="3dk6o0qe9b2a3">
        <p data-id="element-545" data-mp="1cgxe5f6ohh77">Online giving is being set up. Please check back soon!</p>
      </Modal>
    </>;
}